import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Typography, Table, Empty, message } from "antd";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";

import { useApi, ApiNames } from "../../hooks/api";
import { HitlTaskModal } from "./HitlTaskModal";
import { SectionError } from "./SectionError";
import { TableWrap, ActionButton } from "./taskStyles";
import { patientLabel, formatRelativeTime, hitlContextLabel } from "./taskHelpers";
import { selectIsPhiAllowed } from "../../store/selector";
import { useAutoRefetch } from "./useAutoRefetch";
import { HITL_TASK_TYPES } from "../../constant/agenticPlatform";

const { Text } = Typography;

// Server-driven pagination: each page fetches its own limit/offset slice (the
// platform returns { total, items }), so the section pages through ANY backlog
// size. The old fixed first-100 fetch made newer tasks invisible whenever the
// queue exceeded it — oldest-first FIFO puts the newest task LAST (regression:
// a fresh emr_fill task at position ~136 of a flooded pre queue was unreachable).
const PAGE_SIZE = 5;
// One-shot wider fetch for the ?agenticRunId deep link when the target isn't on
// the current page. The platform caps `limit` at 100 — past that the deep link
// degrades to just opening the hub (same reach it had before pagination).
const DEEP_LINK_LOOKUP_LIMIT = 100;

/**
 * One actionable HITL section of the Tasks hub — Consent decisions
 * (`hitlTaskType="consent_decide"`, action "Review") or Manual EMR fill
 * (`hitlTaskType="emr_fill"`, action "Fill EMR data").
 *
 * Renders a patient / context / pending-since / action table (the context
 * column is the mockup's "Reason" for emr_fill / "State" for consent, via
 * `hitlContextLabel`). The whole row — or the action button — opens the
 * shared `HitlTaskModal`, whose body switches on the row's `hitlTaskType`.
 * Reports its total up via `onCountChange` for the collapsible header,
 * auto-refreshes every 1m (paused while a modal is open), and refetches
 * when the hub's `refreshNonce` bumps. Resolving a task optimistically
 * drops its row, then refetches (the resume signal is processed async).
 */
const HitlTasksSection = ({
  hitlTaskType,
  actionLabel,
  contextTitle,
  emptyText,
  onCountChange,
  refreshNonce,
  autoOpenRunId
}) => {
  const { t } = useTranslation();
  // MRN is PHI — only hydrated by FN (and only into the row) for PHI-cleared
  // callers, so gate the column on the same flag the rest of the hub uses.
  const isPhiAllowed = useSelector(selectIsPhiAllowed);

  const [fetchTasks, result, isFetching, fetchError] = useApi(ApiNames.AgenticHitlTasks);
  // Separate instance for the deep-link lookup so it can't clobber the page slice.
  const [lookupTasks, lookupResult] = useApi(ApiNames.AgenticHitlTasks);

  // The run the navigator opened — drives the modal. `null` = closed.
  const [activeItem, setActiveItem] = useState(null);
  // 1-based server page — each change refetches its own limit/offset slice.
  const [page, setPage] = useState(1);
  // Optimistically hide runs just resolved: the workflow processes the
  // resume signal async, so an immediate refetch can still return the run
  // as WAITING_HITL. agenticRunIds are unique auto-increment, never reused.
  const [resolvedRunIds, setResolvedRunIds] = useState(() => new Set());

  // `useApi` hands back a fresh `fetchTasks` identity every render, so we
  // drive the fetch off a ref + stable inputs — never off the function
  // identity, which would loop the request. `refetch` stays stable for the
  // effect, the auto-refresh timer, the resolve, and the hub's refresh.
  const fetchTasksRef = useRef(fetchTasks);
  fetchTasksRef.current = fetchTasks;
  const lookupTasksRef = useRef(lookupTasks);
  lookupTasksRef.current = lookupTasks;
  // Page changes give `refetch` a fresh identity, so `useAutoRefetch` both
  // re-arms and fires immediately — the page fetch and the 1m poll share one path.
  const refetch = useCallback(() => {
    fetchTasksRef.current({ hitlTaskType, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
  }, [hitlTaskType, page]);

  // Initial load + 1m background auto-refresh, paused while a HITL modal is open
  // so a poll doesn't reload the list mid-task.
  useAutoRefetch(refetch, !!activeItem);

  // Manual "Refresh all" from the hub header (skips the initial 0).
  useEffect(() => {
    if (refreshNonce > 0) refetch();
  }, [refreshNonce, refetch]);

  const rows = useMemo(
    () => (result?.items ?? []).filter((item) => !resolvedRunIds.has(item.agenticRunId)),
    [result, resolvedRunIds]
  );

  // Keep the header count consistent with the rows actually shown. The server
  // `total` still includes runs we've optimistically dropped — the resume
  // signal is processed async, so a refetch right after a resolve can still
  // return the just-resolved run. Subtract those, or the badge reads "1" while
  // the table reads "Nothing to ..." until the next auto-refresh reconciles.
  const displayedTotal = useMemo(() => {
    const droppedButStillReturned = (result?.items ?? []).filter((item) =>
      resolvedRunIds.has(item.agenticRunId)
    ).length;
    return Math.max(0, (result?.total ?? 0) - droppedButStillReturned);
  }, [result, resolvedRunIds]);

  // Report the count to the hub for the collapsible header.
  useEffect(() => {
    if (result) onCountChange(displayedTotal);
  }, [result, displayedTotal, onCountChange]);

  // Resolves/timeouts can shrink the queue underneath the current page — snap
  // back to the last non-empty page instead of stranding an empty table.
  useEffect(() => {
    if (!result) return;
    const maxPage = Math.max(1, Math.ceil(displayedTotal / PAGE_SIZE));
    if (page > maxPage) setPage(maxPage);
  }, [result, displayedTotal, page]);

  // One-shot deep-link auto-open: when the hub passes `?agenticRunId` and a row in
  // this section's feed matches, open its modal once. Guarded so refetches
  // (and the other section) don't re-open it after the navigator closes it.
  const autoOpenedRef = useRef(false);
  const lookupFiredRef = useRef(false);
  useEffect(() => {
    if (!autoOpenRunId || autoOpenedRef.current) return;
    // Loose compare — the URL param is a string, the run id is numeric.
    // eslint-disable-next-line eqeqeq
    const target = (result?.items || []).find((item) => item.agenticRunId == autoOpenRunId);
    if (target) {
      autoOpenedRef.current = true;
      setActiveItem(target);
      return;
    }
    // Server pagination means the target may simply be on another page — run a
    // one-shot wider lookup before giving up.
    if (result && !lookupFiredRef.current) {
      lookupFiredRef.current = true;
      lookupTasksRef.current({ hitlTaskType, limit: DEEP_LINK_LOOKUP_LIMIT, offset: 0 });
    }
  }, [result, autoOpenRunId, hitlTaskType]);

  useEffect(() => {
    if (!autoOpenRunId || autoOpenedRef.current) return;
    // eslint-disable-next-line eqeqeq
    const target = (lookupResult?.items || []).find((item) => item.agenticRunId == autoOpenRunId);
    if (target) {
      autoOpenedRef.current = true;
      setActiveItem(target);
    }
  }, [lookupResult, autoOpenRunId]);

  // Resume signal sent (fill saved / consent decided) → close the modal,
  // optimistically drop the row, and refetch so the section reconciles.
  const handleResolved = () => {
    const resolvedRunId = activeItem?.agenticRunId;
    setActiveItem(null);
    if (resolvedRunId != null) {
      setResolvedRunIds((prev) => new Set(prev).add(resolvedRunId));
    }
    refetch();
  };

  const handleError = (msg) => message.error(msg);

  const columns = [
    {
      title: t("agentic_tasks.col_patient"),
      key: "patient",
      // Link navigates in a new tab; stop propagation so it doesn't also
      // open the row's modal.
      render: (_v, row) => (
        <a
          className="who-link"
          href={`/patient/${row.patientId}`}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {patientLabel(row)}
        </a>
      )
    },
    // MRN helps the navigator find the patient in the EMR to fill the data, so
    // it's shown on the manual-fill section only — and only when PHI-cleared
    // (FN hydrates `row.mrn` from patient_phi_datum just for those callers;
    // it's null otherwise, and never stored on the agent side). Mirrors the
    // screenshots-tasks MRN cell.
    ...(hitlTaskType === HITL_TASK_TYPES.EMR_FILL && isPhiAllowed
      ? [
          {
            title: t("agentic_platform.emr_tasks.col_mrn", "MRN"),
            key: "mrn",
            width: 140,
            render: (_v, row) =>
              row.mrn ? (
                <Text
                  className="muted"
                  style={{ fontFamily: "monospace", fontSize: 12 }}
                  copyable={{
                    text: row.mrn,
                    tooltips: [
                      t("agentic_platform.emr_tasks.copy_mrn", "Copy MRN"),
                      t("agentic_platform.emr_tasks.copied_mrn", "Copied")
                    ]
                  }}
                >
                  {row.mrn}
                </Text>
              ) : (
                <span className="muted">—</span>
              )
          }
        ]
      : []),
    {
      title: contextTitle,
      key: "context",
      render: (_v, row) => <span className="muted">{hitlContextLabel(row, t)}</span>
    },
    {
      title: t("agentic_tasks.col_pending_since"),
      key: "awaitingSince",
      width: 140,
      render: (_v, row) => <span className="muted">{formatRelativeTime(row.awaitingSince)}</span>
    },
    {
      title: "",
      key: "action",
      align: "right",
      width: 140,
      render: (_v, row) => (
        <ActionButton
          size="small"
          icon={hitlTaskType === HITL_TASK_TYPES.EMR_FILL ? <EditOutlined /> : <EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            setActiveItem(row);
          }}
        >
          {actionLabel}
        </ActionButton>
      )
    }
  ];

  return (
    <>
      {fetchError && !isFetching ? (
        // A failed fetch must not look like an empty section — surface the
        // error (with Retry) instead of the misleading "Nothing to ..." state.
        <SectionError onRetry={refetch} testId={`agentic-tasks-error-${hitlTaskType}`} />
      ) : !isFetching && rows.length === 0 ? (
        <div style={{ padding: 24 }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
        </div>
      ) : (
        <TableWrap $clickable>
          <Table
            size="small"
            rowKey="agenticRunId"
            columns={columns}
            dataSource={rows}
            loading={isFetching}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: displayedTotal,
              showSizeChanger: false,
              onChange: (nextPage) => setPage(nextPage)
            }}
            onRow={(record) => ({ onClick: () => setActiveItem(record) })}
          />
        </TableWrap>
      )}

      <HitlTaskModal
        item={activeItem}
        onClose={() => setActiveItem(null)}
        onResolved={handleResolved}
        onError={handleError}
      />
    </>
  );
};

export { HitlTasksSection };
